// src/utils/pwaInitializer.js - Enhanced with Config Support

import { notificationService } from '../services/NotificationService';

export class PWAInitializer {
  static isInitialized = false;
  static registration = null;

  /**
   * Initialize PWA features
   */
  static async initialize() {
    if (PWAInitializer.isInitialized) {
      console.log('PWA already initialized');
      return;
    }

    console.log('Initializing PWA features...');

    try {
      // 1. Register Service Worker with config support
      PWAInitializer.registration = await PWAInitializer.registerServiceWorker();

      // 2. Send config to service worker
      await PWAInitializer.updateServiceWorkerConfig();

      // 3. Setup notification service
      await PWAInitializer.setupNotifications();

      // 4. Setup PWA install prompt
      PWAInitializer.setupInstallPrompt();

      // 5. Setup app badge (if supported)
      PWAInitializer.setupAppBadge();

      // 6. Setup background sync (if needed)
      PWAInitializer.setupBackgroundSync();

      PWAInitializer.isInitialized = true;
      console.log('PWA initialization complete');

    } catch (error) {
      console.error('PWA initialization failed:', error);
    }
  }

  /**
   * Register service worker with enhanced config support
   */
  static async registerServiceWorker() {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/' // Service worker will control all pages
        });

        console.log('Service Worker registered:', registration.scope);

        // Listen for service worker updates
        registration.addEventListener('updatefound', () => {
          console.log('Service Worker update found');
          const newWorker = registration.installing;

          newWorker?.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('New service worker available');
              PWAInitializer.showUpdateAvailableNotification();
            }
          });
        });

        // Enhanced service worker message handling
        navigator.serviceWorker.addEventListener('message', (event) => {
          console.log('Message from service worker:', event.data);
          PWAInitializer.handleServiceWorkerMessage(event.data);
        });

        return registration;
      } catch (error) {
        console.error('Service Worker registration failed:', error);
        throw error;
      }
    } else {
      console.warn('Service Worker not supported');
      throw new Error('Service Worker not supported');
    }
  }

  /**
   * Send config to service worker
   */
  static async updateServiceWorkerConfig() {
    if (!window.runtimeConfig) {
      console.warn('Runtime config not loaded yet, retrying...');

      // Wait a bit and retry
      await new Promise(resolve => setTimeout(resolve, 100));

      if (!window.runtimeConfig) {
        console.error('Runtime config still not available');
        return;
      }
    }

    const config = {
      BASE_URL: window.runtimeConfig.BASE_URL
    };

    try {
      // Send to active service worker
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'UPDATE_CONFIG',
          data: config
        });
      }

      // Send to installing service worker
      if (PWAInitializer.registration?.installing) {
        PWAInitializer.registration.installing.postMessage({
          type: 'UPDATE_CONFIG',
          data: config
        });
      }

      // Send to waiting service worker
      if (PWAInitializer.registration?.waiting) {
        PWAInitializer.registration.waiting.postMessage({
          type: 'UPDATE_CONFIG',
          data: config
        });
      }

      console.log('Config sent to service worker:', config);
    } catch (error) {
      console.error('Failed to send config to service worker:', error);
    }
  }

  /**
   * Setup notification service
   */
  static async setupNotifications() {
    try {
      // Initialize notification service
      notificationService.setupNotificationHandlers();

      // Don't auto-request permission - let the app do it when appropriate
      console.log('📱 Notification service ready');

      return true;
    } catch (error) {
      console.error('Notification setup failed:', error);
      return false;
    }
  }

  /**
   * Setup PWA install prompt
   */
  static setupInstallPrompt() {
    let deferredPrompt = null;

    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (event) => {
      console.log('PWA install prompt available');

      // Prevent the mini-infobar from appearing
      event.preventDefault();

      // Store the event for later use
      deferredPrompt = event;
      window.deferredInstallPrompt = deferredPrompt;

      // Optional: Show custom install button
      PWAInitializer.showInstallButton(deferredPrompt);
    });

    // Listen for app installed event
    window.addEventListener('appinstalled', (event) => {
      console.log('PWA installed successfully');
      deferredPrompt = null;
      window.deferredInstallPrompt = null;

      // Optional: Track installation
      PWAInitializer.trackPWAInstall();
    });
  }

  /**
   * Show custom install button (optional)
   */
  static showInstallButton(deferredPrompt) {
    console.log('PWA can be installed');

    // Store in localStorage to show install banner later
    localStorage.setItem('pwa-installable', 'true');

    // Dispatch custom event for components to listen to
    window.dispatchEvent(new CustomEvent('pwa-installable', {
      detail: { deferredPrompt }
    }));
  }

  /**
   * Setup app badge (if supported)
   */
  static setupAppBadge() {
    if ('setAppBadge' in navigator) {
      console.log('App badge supported');

      // Clear badge initially
      navigator.setAppBadge(0).catch(err => {
        console.warn('Could not clear app badge:', err);
      });

      // Global function to update badge count
      window.updateAppBadge = (count) => {
        if ('setAppBadge' in navigator) {
          navigator.setAppBadge(count).catch(err => {
            console.warn('Could not set app badge:', err);
          });
        }

        // Also send to service worker
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'UPDATE_BADGE',
            data: { count }
          });
        }
      };
    } else {
      console.log('App badge not supported');
    }
  }

  /**
   * Setup background sync (if needed)
   */
  static setupBackgroundSync() {
    if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
      console.log('Background sync supported');

      // Register background sync
      navigator.serviceWorker.ready.then(registration => {
        return registration.sync.register('alarm-sync');
      }).catch(err => {
        console.warn('Background sync registration failed:', err);
      });
    } else {
      console.log('Background sync not supported');
    }
  }

  /**
   * Enhanced service worker message handling
   */
  static handleServiceWorkerMessage(data) {
    const { type, action, data: messageData } = data;

    switch (type) {
      case 'GET_CONFIG':
        // Service worker is requesting config
        if (window.runtimeConfig) {
          const config = { BASE_URL: window.runtimeConfig.BASE_URL };
          // Reply with config (if MessageChannel was used)
          if (data.port) {
            data.port.postMessage({
              type: 'CONFIG_RESPONSE',
              data: config
            });
          }
        }
        break;

      case 'NOTIFICATION_CLICKED':
        console.log('Notification clicked:', action, messageData);

        if (action === 'view' || !action) {
          // Focus the window and navigate to appropriate page
          window.focus();

          if (messageData?.type === 'alarm') {
            // Navigate to patients page or specific alarm
            const targetPath = messageData.url || '/my-patients';
            if (window.location.pathname !== targetPath) {
              window.history.pushState({}, '', targetPath);
              window.dispatchEvent(new PopStateEvent('popstate'));
            }
          }
        }

        // Dispatch custom event for app components
        window.dispatchEvent(new CustomEvent('notification-clicked', {
          detail: { action, data: messageData }
        }));
        break;

      case 'BACKGROUND_SYNC':
        console.log('Background sync triggered:', action);

        // Dispatch custom event for app components
        window.dispatchEvent(new CustomEvent('background-sync', {
          detail: { action, data: messageData }
        }));
        break;

      default:
        console.log('Unknown service worker message:', type, data);
    }
  }

  /**
   * Show update available notification
   */
  static showUpdateAvailableNotification() {
    console.log('App update available');

    // Dispatch event for your components to show update notification
    window.dispatchEvent(new CustomEvent('app-update-available'));

    // Optional: Show browser notification
    if (notificationService.isPermitted()) {
      notificationService.showNotification('App Update Available', {
        body: 'A new version of the app is available. Refresh to update.',
        tag: 'app-update',
        actions: [
          { action: 'update', title: 'Update Now' },
          { action: 'later', title: 'Later' }
        ]
      });
    }
  }

  /**
   * Track PWA install (for analytics)
   */
  static trackPWAInstall() {
    try {
      console.log('Tracking PWA installation');

      // Track installation event
      if (window.gtag) {
        window.gtag('event', 'pwa_install', {
          event_category: 'engagement',
          event_label: 'PWA installed'
        });
      }

      // Custom analytics
      if (window.analytics) {
        window.analytics.track('PWA Installed', {
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent
        });
      }
    } catch (error) {
      console.error('Analytics tracking failed:', error);
    }
  }

  /**
   * Manually trigger PWA install
   */
  static async triggerInstall() {
    const deferredPrompt = window.deferredInstallPrompt;

    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        console.log(`PWA install outcome: ${outcome}`);

        window.deferredInstallPrompt = null;
        return outcome === 'accepted';
      } catch (error) {
        console.error('PWA install failed:', error);
        return false;
      }
    } else {
      console.warn('PWA install prompt not available');
      return false;
    }
  }

  /**
   * Check if app is installed as PWA
   */
  static isPWAInstalled() {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = window.navigator.standalone === true;
    return isStandalone || isIOSStandalone;
  }

  /**
   * Get PWA status info
   */
  static getPWAStatus() {
    return {
      serviceWorkerSupported: 'serviceWorker' in navigator,
      notificationSupported: 'Notification' in window,
      installPromptAvailable: !!window.deferredInstallPrompt,
      isInstalled: PWAInitializer.isPWAInstalled(),
      notificationPermission: 'Notification' in window ? Notification.permission : 'unsupported',
      isOnline: navigator.onLine,
      connectionType: navigator.connection?.effectiveType || 'unknown',
      configLoaded: !!window.runtimeConfig,
      apiBaseUrl: window.runtimeConfig?.BASE_URL || 'not loaded'
    };
  }

  /**
   * Force service worker to skip waiting and activate
   */
  static async skipWaiting() {
    if (PWAInitializer.registration?.waiting) {
      PWAInitializer.registration.waiting.postMessage({
        type: 'SKIP_WAITING'
      });
    }
  }

  /**
   * Refresh cache
   */
  static async refreshCache() {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CACHE_REFRESH'
      });
    }
  }

  /**
   * Manual config update (call this when config changes)
   */
  static async onConfigChange() {
    await PWAInitializer.updateServiceWorkerConfig();
  }
}

// Auto-initialize when module is imported
if (typeof window !== 'undefined') {
  // Wait for config to load first
  const initWithConfig = () => {
    if (window.runtimeConfig) {
      PWAInitializer.initialize();
    } else {
      // Retry after a short delay
      setTimeout(initWithConfig, 100);
    }
  };

  // Initialize after DOM is loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWithConfig);
  } else {
    initWithConfig();
  }
}